import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { ClientService, type BillingClientOption } from '@/lib/services/client'
import { SearchableOptions } from './SearchableOptions'

type Props = {
  isOpen: boolean
  onClose: () => void
  onCreate: (clientId: number) => Promise<void>
}

export function CreateRequestFromEmailModal({ isOpen, onClose, onCreate }: Props) {
  const [clients, setClients] = useState<BillingClientOption[]>([])
  const [query, setQuery] = useState('')
  const [clientId, setClientId] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setQuery('')
    setClientId(null)
    let cancelled = false
    setLoading(true)
    ClientService.getClientOptions()
      .then((rows) => {
        if (!cancelled) setClients(rows)
      })
      .catch(() => {
        if (!cancelled) setClients([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen])

  const options = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return clients
      .filter((client) => {
        if (!needle) return true
        return `${client.name} ${client.code}`.toLowerCase().includes(needle)
      })
      .slice(0, 30)
      .map((client) => ({
        value: client.id,
        label: client.name,
        hint: client.code,
      }))
  }, [clients, query])

  const submit = async () => {
    if (!clientId) return
    setSaving(true)
    try {
      await onCreate(clientId)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Ticket Request</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" mb={3}>
            This creates a Ticket Request only. A parent ticket or task is created later, when someone acknowledges the request.
          </Text>
          <SearchableOptions
            label="Client"
            placeholder="Search by client name or code"
            query={query}
            options={options}
            selectedValue={clientId}
            loading={loading}
            emptyText="No clients match that search"
            onQueryChange={(next) => {
              setQuery(next)
              setClientId(null)
            }}
            onSelect={(value) => {
              const client = clients.find((row) => row.id === value)
              setClientId(value)
              setQuery(client?.name ?? '')
            }}
          />
        </ModalBody>
        <ModalFooter>
          <Button mr={2} onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button colorScheme="blue" isDisabled={!clientId} isLoading={saving} onClick={() => void submit()}>
            Create Ticket Request
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
